/**
 * Markdown parsing utilities
 * Wrapper around marked.ts with DOMPurify sanitization
 * Configures custom link rendering for external/internal links
 */

import { Marked, Renderer } from 'marked'
import DOMPurify from 'dompurify'

// Configure marked instance (marked 17.x compatible)
const marked = new Marked({
  breaks: true,  // Convert \n to <br>
  gfm: true,     // GitHub Flavored Markdown
})

// Custom link renderer
const renderer = new Renderer()
renderer.link = ({ href, text }): string => {
  const styles = 'tw:cursor-pointer tw:hover:underline tw:text-blue-700'
  
  // Internal 'getstarted' links open in same tab
  if (href?.includes('getstarted')) {
    return `<a href="${href}" class="${styles}" data-link="true">${text ?? href}</a>`
  }
  
  // External links open in new tab with security attributes
  return `<a href="${href}" class="${styles}" target="_blank" rel="noopener noreferrer" data-link="true">${text ?? href}</a>`
}

marked.use({ renderer })

/**
 * Parse markdown text to sanitized HTML
 * @param text - Markdown text to parse
 * @returns Sanitized HTML string safe for rendering
 * @example
 * const html = parseMarkdown('**Bold** text with [link](https://example.com)')
 * // Returns: '<strong>Bold</strong> text with <a href="...">link</a>'
 */
export function parseMarkdown(text: string): string {
  const html = marked.parse(text) as string
  
  return DOMPurify.sanitize(html, {
    ADD_ATTR: ['target', 'rel', 'data-link'],
  })
}